import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getAccessToken, useAuthStore } from '../store/auth.store';

/**
 * Global notification socket (N8): mirrors the Live wall WS pattern
 * (URL building, bearer sub-protocol, backoff reconnect) but handles only
 * `type:"notification"` messages published by producers (N4).
 *
 * On event: refresh the unread badge + lists, and toast when the row is
 * addressed to the current user. Failures are silent — 15s polling in the
 * Bell is the fallback transport.
 */
export function useNotificationSocket() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);

  useEffect(() => {
    const token = accessToken ?? getAccessToken();
    if (!token || !user) return;

    let orgId = '';
    try {
      orgId = JSON.parse(atob(token.split('.')[1])).org_id;
    } catch {
      return;
    }
    if (!orgId) return;
    const myId = user.id;

    const apiUrl: string = (import.meta as unknown as { env?: Record<string, string | undefined> }).env
      ?.VITE_API_URL ?? '';
    const wsBase = apiUrl
      ? apiUrl.replace(/^http/, 'ws')
      : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;
    const wsUrl = `${wsBase.replace(/\/$/, '')}/ws?org_id=${encodeURIComponent(orgId)}`;

    let ws: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    let attempts = 0;
    let closed = false;
    const MAX_DELAY_MS = 30000;

    const scheduleReconnect = () => {
      if (closed) return;
      const delay = Math.min(1000 * 2 ** attempts, MAX_DELAY_MS);
      attempts += 1;
      window.clearTimeout(reconnectTimer);
      reconnectTimer = window.setTimeout(connect, delay);
    };

    const connect = () => {
      if (closed) return;
      try {
        ws = new WebSocket(wsUrl, ['bearer', token]);
      } catch {
        scheduleReconnect();
        return;
      }

      ws.onopen = () => {
        attempts = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data?.type !== 'notification' || !data?.notification) return;
          const n = data.notification as {
            id?: string;
            recipient_id?: string;
            payload?: { title?: string; body?: string };
          };
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
          if (n.recipient_id && n.recipient_id === myId) {
            toast.info(n.payload?.title || 'New notification', {
              description: n.payload?.body,
            });
          }
        } catch {
          // ignore malformed frames
        }
      };

      ws.onerror = () => {
        try {
          ws?.close();
        } catch {
          // ignore
        }
      };

      ws.onclose = () => {
        if (!closed) scheduleReconnect();
      };
    };

    const handleOnline = () => {
      attempts = 0;
      connect();
    };
    window.addEventListener('online', handleOnline);

    connect();

    return () => {
      closed = true;
      window.clearTimeout(reconnectTimer);
      window.removeEventListener('online', handleOnline);
      try {
        ws?.close();
      } catch {
        // ignore
      }
    };
  }, [accessToken, queryClient, user]);
}
